import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ShoppingCart, Package, FileText, MessageSquare, TrendingUp,
  Search, Heart, Clock, CheckCircle, AlertCircle,
  Building2, ArrowRight, Send
} from 'lucide-react';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, activityRes, profileRes] = await Promise.all([
        axios.get(`${API}/buyer/dashboard/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/buyer/dashboard/recent-activity`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/buyer/profile`, { headers }).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setBuyerProfile(profileRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      responded: 'bg-blue-100 text-blue-700 border-blue-200',
      negotiating: 'bg-purple-100 text-purple-700 border-purple-200',
      accepted: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return statusStyles[status] || statusStyles.pending;
  };

  const quickActions = [
    { label: 'Search Products', href: '/marketplace', icon: Search, color: 'bg-continental' },
    { label: 'My Inquiries', href: '/buyer/inquiries', icon: MessageSquare, color: 'bg-blue-600' },
    { label: 'Saved Suppliers', href: '/buyer/saved-suppliers', icon: Heart, color: 'bg-pink-600' },
    { label: 'My Orders', href: '/buyer/orders', icon: ShoppingCart, color: 'bg-tradeGold' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-continental border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex">
        {/* Shared Collapsible Sidebar */}
        <DashboardSidebar />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8" data-testid="buyer-dashboard">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-charcoal">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-gray-600 mt-1">Discover verified African suppliers and grow your business</p>
          </div>

          {/* Profile Completion Alert */}
          {!buyerProfile && (
            <Card className="mb-6 border-blue-200 bg-blue-50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Complete your buyer profile</p>
                      <p className="text-sm text-blue-700">Get verified to access all platform features</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/buyer/onboarding')} 
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Complete Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Link to="/buyer/inquiries">
              <Card className="hover:shadow-lg hover:border-continental transition-all cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Inquiries Sent</p>
                      <p className="text-2xl font-bold text-charcoal">{stats?.inquiries_sent || 0}</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Send className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/buyer/inquiries">
              <Card className="hover:shadow-lg hover:border-continental transition-all cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Responses</p>
                      <p className="text-2xl font-bold text-charcoal">{stats?.inquiries_responded || 0}</p>
                    </div>
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/buyer/orders">
              <Card className="hover:shadow-lg hover:border-continental transition-all cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Orders Placed</p>
                      <p className="text-2xl font-bold text-charcoal">{stats?.orders_placed || 0}</p>
                    </div>
                    <div className="w-10 h-10 bg-tradeGold/20 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-tradeGold" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/buyer/orders">
              <Card className="hover:shadow-lg hover:border-continental transition-all cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Purchases</p>
                      <p className="text-2xl font-bold text-charcoal">
                        ${(stats?.total_purchase_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-continental/10 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-continental" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-charcoal mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <Link key={action.label} to={action.href}>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                    <CardContent className="p-4 flex flex-col items-center text-center">
                      <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mb-3`}>
                        <action.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="font-medium text-charcoal">{action.label}</span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Inquiries */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Recent Inquiries</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/buyer/inquiries')}>
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {recentActivity?.recent_inquiries?.length > 0 ? (
                  <div className="space-y-3">
                    {recentActivity.recent_inquiries.slice(0, 5).map((inquiry) => (
                      <div
                        key={inquiry.inquiry_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => navigate(`/buyer/inquiries/${inquiry.inquiry_id}`)}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-charcoal text-sm">{inquiry.product_name}</p>
                          <p className="text-xs text-gray-500">To: {inquiry.seller_name}</p>
                        </div>
                        <Badge className={`text-xs ${getStatusBadge(inquiry.status)}`}>
                          {inquiry.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No inquiries yet</p>
                    <Button 
                      variant="link" 
                      onClick={() => navigate('/marketplace')}
                      className="text-continental"
                    >
                      Browse marketplace to find products
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Recent Orders</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/buyer/orders')}>
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {recentActivity?.recent_orders?.length > 0 ? (
                  <div className="space-y-3">
                    {recentActivity.recent_orders.map((order) => (
                      <div
                        key={order.order_id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-charcoal text-sm">{order.product_name}</p>
                          <p className="text-xs text-gray-500">
                            {order.quantity} units • ${order.total_amount?.toLocaleString()}
                          </p>
                        </div>
                        <Badge className={`text-xs ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {order.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No orders yet</p>
                    <p className="text-sm text-gray-400">Orders will appear here once you accept a quote</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="mt-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardContent className="py-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold mb-2">Ready to source from Africa?</h3>
                  <p className="text-blue-100">
                    Discover thousands of verified suppliers across 54 African countries
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/marketplace')}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Browse Marketplace
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default BuyerDashboard;
